import { differenceInYears } from "date-fns";
import merge from "lodash/merge";
import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { CURRENT_COOKIES_VERSION, LS } from "@/constants/index";
import type { DeepPartial, ICookieConsent } from "@/types/common";
import { AuthState } from "@/types/common";
import type { IUser } from "@/types/users";
import { UserLoadingStatus } from "@/types/users";
import { deepMerge } from "@/utils/deepMerge";
import type { IAppSlice, IAppState } from "./types";

const cookiesLS = localStorage.getItem(LS.Cookies);
const cookies: ICookieConsent = cookiesLS
  ? JSON.parse(cookiesLS)
  : {
      essential: {
        all: true,
      },
      optional: {
        analytical: false,
      },
    };
const cookiesVersionLS = localStorage.getItem(LS.CookiesVersion);
const cookiesVersion = cookiesVersionLS
  ? JSON.parse(cookiesVersionLS)
  : undefined;
const noConsentGiven = !localStorage.getItem(LS.Cookies);
const cookieVersionChanged =
  cookiesVersion?.v !== CURRENT_COOKIES_VERSION.toString();
const cookiesExpired = cookiesVersion
  ? differenceInYears(Date.now(), cookiesVersion.d) > 1
  : false;

export const initialState: IAppState = {
  user: null,
  userLoaded: UserLoadingStatus.NotStarted,
  accessToken: "",
  authenticated: AuthState.Loading,
  loading: false,
  cookies,
  showCookiesBanner: noConsentGiven || cookieVersionChanged || cookiesExpired,
  savedUrl: null,
  navigationState: undefined,
  userTenants: [],
  userTenantsLoaded: false,
};

export const useAppStore = create<IAppSlice>()(
  immer((set) => ({
    ...initialState,
    setLoading: (loading: boolean) => {
      set((state) => {
        state.loading = loading;
      });
    },
    setAccessToken: (accessToken: string) => {
      set((state) => {
        state.accessToken = accessToken;
      });
    },
    setAuthenticated: (authenticated: AuthState) => {
      set((state) => {
        state.authenticated = authenticated;
      });
    },
    setUser: (user: IUser | null) => {
      set((state) => {
        state.user = user;
        state.userLoaded = UserLoadingStatus.Loaded;
      });
    },
    setUserLoaded: (userLoaded: UserLoadingStatus) => {
      set((state) => {
        state.userLoaded = userLoaded;
      });
    },
    updateUser: (p: DeepPartial<IUser>) => {
      set((state) => {
        state.user = state.user ? deepMerge(state.user, p) : null;
      });
    },
    setCookies: (nextCookies: DeepPartial<ICookieConsent> | undefined) => {
      set((state) => {
        if (state.cookies) {
          state.cookies = { ...merge(state.cookies, nextCookies) };
        }
      });
    },
    setShowCookiesBanner: (showCookiesBanner: boolean) => {
      set((state) => {
        state.showCookiesBanner = showCookiesBanner;
      });
    },
    setSavedUrl: (url: string | null) => {
      set((state) => {
        state.savedUrl = url;
      });
    },
    setNavigationState: (p: any) => {
      set((state) => {
        state.navigationState = p;
      });
    },
    clearNavigationState: (field?: string) => {
      set((state) => {
        if (!field) {
          state.navigationState = undefined;
        } else if (state.navigationState) {
          delete state.navigationState[field];
        }
      });
    },
    setUserTenants: (userTenants: string[]) => {
      set((state) => {
        state.userTenants = userTenants;
        state.userTenantsLoaded = true;
      });
    },
    setUserTenantsLoaded: (p: boolean) => {
      set((state) => {
        state.userTenantsLoaded = p;
      });
    },
  })),
);
